import Lottie from 'lottie-react'
import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {FeatureBlock} from 'entities/credits'
import {
  PaymentStatus,
  setConfirmPayModal,
  setPaymentModal,
  usePaymentError,
  usePaymentStatusModal,
  useProcessPackage
} from 'entities/payment'
import {FreshChatOpenSupport} from 'features/freshChat'
import {Button} from 'shared/ui/buttons'
import {Error} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader/ui'
import {Modal} from 'shared/ui/modal'
import confettiAnimation from './img/confetti.json'
import styles from './styles.module.scss'

const TIMEOUT = 10000

export const PaymentStatusModal: FC = () => {
  const dispatch = useDispatch()
  const {isOpen, status} = usePaymentStatusModal()

  const onCloseHandler = () => dispatch(setPaymentModal({isOpen: false}))

  const getBlock = useMemo(() => {
    switch (status) {
    case PaymentStatus.SUCCESS:
      return <SuccessModal onCloseHandler={onCloseHandler} />
    case PaymentStatus.IN_PROGRESS:
      return <InProgressModal onCloseHandler={onCloseHandler} />
    case PaymentStatus.FAILED:
      return <FailedModal onCloseHandler={onCloseHandler} />
    case PaymentStatus.SEPA_IN_PROGRESS:
      return <PaymentProcessingModal onCloseHandler={onCloseHandler} />
    }
  }, [status, onCloseHandler])

  return (
    <Modal open={isOpen} onClose={onCloseHandler}>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          {getBlock}
        </div>
      </div>
    </Modal>
  )
}

type ModalProps = {onCloseHandler: () => void}

const SuccessModal: FC<ModalProps> = ({onCloseHandler}) => {
  const [showConfetti, setShowConfetti] = useState(true)
  const processPackage = useProcessPackage()

  return (
    <>
      {showConfetti ?
        <Lottie
          animationData={confettiAnimation}
          loop={false}
          className={styles.confetti}
          onComplete={() => setShowConfetti(false)}
        />
        : null}
      <h1 className={styles.title}>Congratulations!</h1>
      <div className={styles.content}>
        <h5 className={styles.description}>You have purchased the "{processPackage.name}" package</h5>
        <div className={styles.content}>
          <FeatureBlock
            credits={processPackage.credits}
            stickers={processPackage.stickers}
            photos={processPackage.photos}
            videos={processPackage.videos}
            virtualGifts={processPackage.virtualGifts}
          />
        </div>
      </div>

      <Button styleType='primary' onClick={onCloseHandler}>Back to site</Button>
    </>
  )
}

const InProgressModal: FC<ModalProps> = ({onCloseHandler}) => {
  const [isTimeOver, setIsTimeOver] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      clearTimeout(timeout)
      setIsTimeOver(true)
    }, TIMEOUT)

    return () => {
      clearTimeout(timeout)
    }
  }, [isTimeOver])


  return (
    <>
      <h1 className={styles.title}>In progress</h1>
      <div className={styles.checking}>
        <p className={styles.checking__text}>Checking transaction</p>
        <Loader />
      </div>
      {
        isTimeOver ? <>
          <p className={styles.message}>
            The process may take some time. You can return to the site as soon as the status changes we will notify you.
          </p>
          <Button styleType='primary' onClick={onCloseHandler}>Back to site</Button>
        </> : null
      }

      <ModalFooter />
    </>
  )
}

const FailedModal: FC<ModalProps> = ({onCloseHandler}) => {
  const error = usePaymentError()

  return (
    <>
      <h1 className={styles.title}>Payment is unsuccessful</h1>
      <div className={styles.checking}>
        <p className={styles.checking__text}>Failed</p>
        <Error />
      </div>

      <div className={styles.error}>
        <p className={styles.error__primary}>General decline 0.03</p>
        {error ?
          <span className={styles.error__description}>
            {error}
          </span>
          : null
        }
      </div>

      <Button styleType='primary' onClick={onCloseHandler}>Back to site</Button>
      <ModalFooter />
    </>
  )
}

const PaymentProcessingModal: FC<ModalProps> = ({onCloseHandler}) => {
  const dispatch = useDispatch()

  const onClickHandler = ()=>{
    onCloseHandler()
    dispatch(setConfirmPayModal({isOpen: true}))
  }

  return (
    <>
      <h1 className={styles.title}>The payment tab is open</h1>
      <div className={styles.checking}>
        <p className={styles.checking__text}>Payment processing</p>
        <Loader />
      </div>
      <p className={styles.message}>
        Please complete the payment in the opened tab of your browser or cancel using the button below.
      </p>
      <Button styleType='primary' onClick={onClickHandler}>Cancel payment</Button>
      <ModalFooter />
    </>
  )
}

const ModalFooter: FC = () =>
  <h5 className={styles.footer}>
    Having trouble making a payment? Contact our
    <span className={styles.footer__support} onClick={FreshChatOpenSupport}> support team</span>
  </h5>